import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import IgitaboLogo from "./IgitaboLogo";

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

// iOS/iPadOS never fires beforeinstallprompt — Add to Home Screen only
// exists behind the OS share sheet, so we fall back to showing instructions
// instead of a tappable native prompt. iPadOS 13+ reports as "MacIntel" but
// is touch-capable, unlike a real Mac.
const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
};

// Layouts with their own fixed/sticky top elements (sidebars, top bars) read
// this custom property to offset themselves below the banner instead of
// being covered by it. Cleared whenever the banner isn't shown.
const BANNER_HEIGHT_VAR = "--pwa-banner-h";

// Nudge heuristics — never right as the page loads, and a "no" is
// remembered instead of re-asking on every login.
const VISIT_KEY = "igitabo_pwa_visits";
const DISMISS_KEY = "igitabo_pwa_dismissed_at";
const MIN_VISITS_BEFORE_PROMPT = 1;
const DISMISS_SNOOZE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
const SHOW_DELAY_MS = 4000;

const isEligibleToPrompt = () => {
  try {
    const visits = Number(localStorage.getItem(VISIT_KEY) || 0) + 1;
    localStorage.setItem(VISIT_KEY, String(visits));

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_SNOOZE_MS) return false;

    return visits >= MIN_VISITS_BEFORE_PROMPT;
  } catch {
    // localStorage unavailable (private mode, etc.) — fall back to allowing it
    return true;
  }
};

const PWAInstallBanner = () => {
  const [show, setShow] = useState(false);
  const [closing, setClosing] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [iosSteps, setIosSteps] = useState(false);
  const barRef = useRef(null);
  const ios = useRef(isIOS());

  useEffect(() => {
    // Already installed — never show
    if (isStandalone()) return;
    if (!isEligibleToPrompt()) return;

    let timer;
    const reveal = () => { timer = setTimeout(() => setShow(true), SHOW_DELAY_MS); };

    // iOS/iPadOS never fires beforeinstallprompt — there's nothing to wait
    // for, so surface the share-sheet instructions on the same schedule.
    if (ios.current) {
      reveal();
      return () => clearTimeout(timer);
    }

    // Prompt already captured in index.html before React loaded
    if (window.__pwaInstallPrompt) {
      reveal();
      return () => clearTimeout(timer);
    }

    // Fallback: prompt fires after React mounts
    const handler = (e) => {
      e.preventDefault();
      window.__pwaInstallPrompt = e;
      reveal();
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      clearTimeout(timer);
    };
  }, []);

  // Hide only when actually installed
  useEffect(() => {
    const onInstalled = () => setShow(false);
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  // Publish the banner's real rendered height so fixed/sticky layout
  // elements (dashboard sidebars, top bars) can push themselves below it
  // instead of being hidden underneath it.
  useLayoutEffect(() => {
    if (!show || closing || !barRef.current) return;
    const root = document.documentElement;
    const el = barRef.current;

    const applyHeight = () => {
      root.style.setProperty(BANNER_HEIGHT_VAR, `${el.offsetHeight}px`);
    };
    applyHeight();

    const ro = new ResizeObserver(applyHeight);
    ro.observe(el);
    window.addEventListener("resize", applyHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", applyHeight);
    };
  }, [show, closing]);

  // Clear the offset the moment the banner starts closing or unmounts,
  // so layouts snap back up immediately rather than waiting on the exit animation.
  useEffect(() => {
    if (!show || closing) {
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, "0px");
    }
    return () => {
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, "0px");
    };
  }, [show, closing]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setClosing(true);
    setTimeout(() => setShow(false), 260);
  }, []);

  const handleInstall = useCallback(async () => {
    // iOS has no programmatic install API — the most we can do is reveal
    // the share-sheet steps in place rather than dismissing the banner.
    if (ios.current) {
      setIosSteps((v) => !v);
      return;
    }

    const prompt = window.__pwaInstallPrompt;
    if (!prompt) return;
    setInstalling(true);
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.__pwaInstallPrompt = null;
    setInstalling(false);
    // Either way the captured prompt is now spent — a lingering banner
    // with a dead button is worse than snoozing and asking again later.
    dismiss();
  }, [dismiss]);

  if (!show) return null;

  return (
    <>
      <style>{`
        @keyframes pwaSlideDownIn {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);      opacity: 1; }
        }
        @keyframes pwaSlideUpOut {
          from { transform: translateY(0);      opacity: 1; }
          to   { transform: translateY(-100%);  opacity: 0; }
        }
        .pwa-install-bar {
          animation: pwaSlideDownIn 0.38s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .pwa-install-bar.closing {
          animation: pwaSlideUpOut 0.24s cubic-bezier(0.4, 0, 1, 1) forwards;
        }
        .pwa-install-btn {
          transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease, opacity 0.15s ease;
        }
        .pwa-install-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          filter: brightness(1.04);
          box-shadow: 0 6px 20px rgba(0,0,0,0.25);
        }
        .pwa-install-btn:active:not(:disabled) {
          transform: scale(0.96) translateY(0);
          filter: brightness(0.97);
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }
        .pwa-install-btn:disabled {
          opacity: 0.65;
          cursor: default;
        }
        .pwa-dismiss-btn {
          transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
        }
        .pwa-dismiss-btn:hover {
          background: rgba(255,255,255,0.15);
          color: #ffffff;
        }
        .pwa-dismiss-btn:active {
          transform: scale(0.9);
        }
        .pwa-info-row {
          cursor: pointer;
          border-radius: 12px;
          transition: background 0.15s ease;
        }
        .pwa-info-row:hover {
          background: rgba(255,255,255,0.08);
        }
        .pwa-info-row:hover .pwa-icon-wrap {
          transform: scale(1.05);
        }
        .pwa-info-row:active .pwa-icon-wrap {
          transform: scale(0.97);
        }
        .pwa-icon-wrap {
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        @supports (padding: max(0px)) {
          .pwa-install-bar {
            padding-top: max(10px, env(safe-area-inset-top));
          }
        }
      `}</style>

      <div
        ref={barRef}
        className={`pwa-install-bar${closing ? " closing" : ""}`}
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          top: 0,
          zIndex: 9999,
          background: "linear-gradient(135deg, #0B3D91 0%, #0047AB 60%, #093278 100%)",
          borderBottom: "3px solid #FAD201",
          boxShadow: "0 8px 32px rgba(15,23,42,0.25)",
          padding: "10px 12px",
        }}
      >
        <div
          style={{
            maxWidth: 560,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <button
            onClick={dismiss}
            className="pwa-dismiss-btn"
            aria-label="Funga"
            style={{
              flexShrink: 0,
              width: 30,
              height: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "transparent",
              border: "none",
              borderRadius: "50%",
              color: "rgba(255,255,255,0.75)",
              fontSize: "1rem",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <i className="bi bi-x-lg" style={{ fontSize: "0.8rem" }} />
          </button>

          <div
            className="pwa-info-row"
            role="button"
            tabIndex={0}
            onClick={handleInstall}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleInstall()}
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "4px 6px",
              margin: "-4px -6px",
            }}
          >
            <div className="pwa-icon-wrap" style={{ flexShrink: 0, borderRadius: 12, overflow: "hidden" }}>
              <IgitaboLogo variant="icon" size={38} />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: "0.86rem",
                  color: "#ffffff",
                  lineHeight: 1.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Inkingi
              </div>
              <div
                style={{
                  fontWeight: 500,
                  fontSize: "0.72rem",
                  color: "rgba(255,255,255,0.72)",
                  marginTop: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {ios.current
                  ? "Kanda hano kugira ngo ubone uko wayishyiraho"
                  : "Shyiraho porogaramu kuri terefone yawe"}
              </div>
            </div>
          </div>

          <button
            onClick={handleInstall}
            disabled={installing}
            className="pwa-install-btn"
            style={{
              flexShrink: 0,
              padding: "8px 16px",
              borderRadius: 999,
              background: "#FAD201",
              border: "none",
              color: "#093278",
              fontWeight: 800,
              fontSize: "0.78rem",
              letterSpacing: "0.01em",
              cursor: installing ? "default" : "pointer",
              boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
              minHeight: 32,
            }}
          >
            {installing ? "..." : ios.current ? "Reba uko" : "Shyiraho"}
          </button>
        </div>

        {ios.current && iosSteps && (
          <div
            style={{
              maxWidth: 560,
              margin: "10px auto 0",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 12,
              padding: "10px 14px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#fff", fontSize: "0.78rem", fontWeight: 600 }}>
              <i className="bi bi-box-arrow-up" style={{ color: "#FAD201" }} />
              1. Kanda ikimenyetso &quot;Gusangiza&quot; (Share) hepfo cyangwa hejuru ya Safari
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#fff", fontSize: "0.78rem", fontWeight: 600 }}>
              <i className="bi bi-plus-square" style={{ color: "#FAD201" }} />
              2. Hitamo &quot;Ongeraho kuri Home Screen&quot; (Add to Home Screen)
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PWAInstallBanner;
